import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { CSVLink } from 'react-csv';
import { useTranslation } from 'react-i18next';
import { Layout, Input, DatePicker, Table, Space, Pagination, Typography, Tag, message, Button, Drawer, Form, Modal, Card } from 'antd';
import { EditOutlined, CheckCircleOutlined, PrinterOutlined, FileExcelOutlined, ReloadOutlined } from '@ant-design/icons';
import withRoleAccess from '../../hoc/withRoleAccess';
import './Registrations.css';
import dayjs from 'dayjs';
import FeedbackPrintModal from './FeedbackPrintModal';
import hash from 'hash-sum';

const { Content } = Layout;
const { Title } = Typography;
const { RangePicker } = DatePicker;

const StudentFeedbackReport = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchTeacher, setSearchTeacher] = useState('');
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printRecord, setPrintRecord] = useState(null);
  const [editDrawerVisible, setEditDrawerVisible] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackFormData, setFeedbackFormData] = useState({
    warmup: '',
    task_1: '',
    task_2: '',
    task_3: '',
    homework: '',
    reflection: '',
    comment: ''
    });
  const { t } = useTranslation();
  const API_URL = process.env.REACT_APP_API_URL;

  const itemsPerPage = 50;


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? 'Invalid date' : date.toLocaleDateString(t('locale'), { day: '2-digit', month: 'short', year: '2-digit' }).toUpperCase();
  };

const fetchFeedbacks = useCallback(async () => {
  try {
    const token = localStorage.getItem('token');
    const res = await axios.get(`${API_URL}/student-feedback`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setFeedbacks(res.data);
  } catch (err) {
    console.error('❌ Error fetching feedbacks:', err);
  }
}, [API_URL]);


useEffect(() => {
  fetchFeedbacks();
}, [fetchFeedbacks]);

useEffect(() => {
  let filtered = feedbacks;

  if (searchTerm) {
    const lowerSearch = searchTerm.toLowerCase();
    filtered = filtered.filter(f =>
      f.student_nickname?.toLowerCase().includes(lowerSearch) ||
      f.student_firstname?.toLowerCase().includes(lowerSearch) ||
      f.student_lastname?.toLowerCase().includes(lowerSearch)
    );
  }

  // Replace feedback ref search with teacher search
  if (searchTeacher) {
    const lowerSearch = searchTeacher.toLowerCase();
    filtered = filtered.filter(f =>
      f.teacher_nickname?.toLowerCase().includes(lowerSearch) ||
      f.teacher_firstname?.toLowerCase().includes(lowerSearch) ||
      f.teacher_lastname?.toLowerCase().includes(lowerSearch)
    );
  }

  if (fromDate) {
    filtered = filtered.filter(f => new Date(f.date) >= fromDate);
  }

  if (toDate) {
    filtered = filtered.filter(f => new Date(f.date) <= toDate);
  }

  setFilteredFeedbacks(filtered);
  setCurrentPage(1);
}, [searchTerm, searchTeacher, fromDate, toDate, feedbacks]);


const handleEdit = async (record) => {
  const token = localStorage.getItem('token');
  try {
    const res = await axios.get(
      `${API_URL}/student-feedback/by-classid-student/${record.classid}/${record.student_id}`,
      {
        headers: { Authorization: `Bearer ${token}` }
      }
    );

    const feedback = res.data;
    setSelectedFeedback(feedback);
    setFeedbackFormData({
      warmup: feedback.warmup || '',
      task_1: feedback.task_1 || '',
      task_2: feedback.task_2 || '',
      task_3: feedback.task_3 || '',
      homework: feedback.homework || '',
      reflection: feedback.reflection || '',
      comment: feedback.comment || ''
    });
    setEditDrawerVisible(true);
  } catch (err) {
    console.error('Failed to fetch feedback for editing:', err);
    message.error('Could not load feedback for editing.');
  }
};

const handleSubmitFeedbackUpdate = async () => {
  if (!selectedFeedback?.id) return;

  try {
    const token = localStorage.getItem('token');
    await axios.put(`${API_URL}/student-feedback/${selectedFeedback.id}`, feedbackFormData, {
      headers: { Authorization: `Bearer ${token}` }
    });

    message.success('Feedback updated successfully');
    
    setEditDrawerVisible(false); // Hide drawer first
    await fetchFeedbacks();      // ✅ Refetch feedbacks after closing
  } catch (error) {
    console.error('Failed to update feedback:', error);
    message.error('Failed to update feedback');
  }
};


const handleApprove = (record) => {
  const currentStatus = record.approve;
  const action = currentStatus === 'approved' ? 'revert to pending' : 'mark as approved';

  Modal.confirm({
    title: 'Confirm Approval Change',
    content: `Are you sure you want to ${action} for this feedback?`,
    okText: 'Yes',
    cancelText: 'No',
    onOk: async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.put(
          `${API_URL}/student-feedback/approve/${record.id}`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const newStatus = res.data.status;
        message.success(`Feedback marked as "${newStatus}"`);

        await fetchFeedbacks(); 
      } catch (error) {
        console.error('Error toggling feedback status:', error);
        message.error('Failed to update approval status');
      }
    }
  });
};

  const generateFeedbackUID = (feedback) => {
  return hash({
    id: feedback.id,                       
    classid: feedback.classid,
    student_id: feedback.student_id,
    teacher_id: feedback.teacher_id,
    created_at: feedback.created_at,
  });
};

  const currentItems = filteredFeedbacks.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const columns = [
    {
    title: t('ClassId'),
    dataIndex: 'classid',
    key: 'classid',
    ellipsis: true
  },
  {
    title: t('Class Code'),
    dataIndex: 'class_code',
    key: 'class_code',
    ellipsis: true,
    width: 200
  },
  {
    title: t('Subject'),
    dataIndex: 'subject_name',
    key: 'subject_name',
    ellipsis: true
  },
{
  title: t('Date - Time'),
  key: 'date',
  ellipsis: true,
  width: 200,
  render: (record) => {
    const timeStr = record.schedule_time;
    const minutes = Number(record.schedule_hour);
    const date = dayjs(record.date); // ← use `record.date` here

    const [hour, min] = timeStr.split(':');
    const start = new Date(2000, 0, 1, +hour, +min);
    const end = new Date(start.getTime() + minutes * 60000);

    const pad = (n) => n.toString().padStart(2, '0');

    return (
      <div>
        <div>{date.format('DD MMM YY')}</div>
        <div>{`${pad(start.getHours())}:${pad(start.getMinutes())} - ${pad(end.getHours())}:${pad(end.getMinutes())}`}</div>
        <div style={{ fontSize: '11px', color: '#888' }}>
          {t('Duration')}: {minutes} {t('minutes')}
        </div>
      </div>
    );
  }
},
 {
  title: t('Teacher'),
  key: 'teacher',
  ellipsis: true,
  width: 200,
  render: (record) => (
    <div>
      <div>{`${record.teacher_firstname || ''} ${record.teacher_lastname || ''}`.trim()}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.teacher_nickname ? `(${record.teacher_nickname})` : ''}
      </div>
    </div>
  )
},
  {
  title: t('Student'),
  key: 'student',
  ellipsis: true,
  width: 200,
  render: (record) => (
    <div>
      <div>{`${record.student_firstname || ''} ${record.student_lastname || ''}`.trim()}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.student_nickname ? `(${record.student_nickname})` : ''}
      </div>
    </div>
  )
},
  {
    title: t('Approval'),
    dataIndex: 'approve',
    key: 'approve',
    render: (status) =>
      status === 'approved' ? (
        <Tag color="green">Approved</Tag>
      ) : (
        <Tag color="orange">Pending</Tag>
      ),
    ellipsis: true
  },
        {
  title: t('Warm Up'),
  dataIndex: 'warmup',
  key: 'warmup',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},

      {
  title: t('Task 1'),
  dataIndex: 'task_1',
  key: 'task_1',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Task 2'),
  dataIndex: 'task_2',
  key: 'task_2',
  render: (text) => (
    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Task 3'),
  dataIndex: 'task_3',
  key: 'task_3',
  render: (text) => (
     <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Homework'),
  dataIndex: 'homework',
  key: 'homework',
  render: (text) => (
     <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Reflection'),
  dataIndex: 'reflection',
  key: 'reflection',
  render: (text) => (
     <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
  title: t('Comment'),
  dataIndex: 'comment',
  key: 'comment',
  render: (text) => (
     <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', width: '300px' }}>{text}</div>
  )
},
{
    title: t('created_by'),
    dataIndex: 'created_by',
    key: 'created_by',
    ellipsis: true,
    width: 150
  },
  {
    title: t('updated_by'),
    dataIndex: 'updated_by_name',
    key: 'updated_by_name',
    ellipsis: true,
    width: 150,
     render: (text, record) => (
    <div>
      <div>{text || '-'}</div>
      <div style={{ fontSize: '11px', color: '#888' }}>
        {record.updated_on ? formatDate(record.updated_on) : ''}
      </div>
    </div>
  )
  },
  {
    title: t('approved_by'),
    dataIndex: 'approved_by_name',
    key: 'approved_by_name',
    ellipsis: true,
    width: 150
  },
  {
  title: t('Feedback Ref'),
  key: 'feedback_ref',
  width: 150,
  render: (_, record) => (
    <div style={{ fontSize: '12px', color: '#555' }}>
      {generateFeedbackUID(record)}
    </div>
  )
},

{
  title: t('Action'),
  key: 'action',
  fixed: 'right',
  render: (record) => (
    <Space>
      <Button
        type="link"
        icon={<EditOutlined />}
        onClick={() => handleEdit(record)}
        aria-label="Edit"
      />
      <Button
        type="link"
        icon={<CheckCircleOutlined />}
        onClick={() => handleApprove(record)}
        aria-label="Approve"
      />
      <Button
        type="link"
        icon={<PrinterOutlined />}
        onClick={() => {
            setPrintRecord(record);
            setPrintModalVisible(true);
        }}
        aria-label="Print"
        />
    </Space>
  )
}

  ];

  const handleDateRangeChange = (dates) => {
    setFromDate(dates ? dates[0].toDate() : null);
    setToDate(dates ? dates[1].toDate() : null);
  };

  return (
    <Layout>
      <Layout>
        <Card style={{ marginBottom: 16 }}>
                    <Title level={3}>
                      <EditOutlined  /> {t('Student Feedback')}</Title>
        <Content>
        

          <Space style={{ marginBottom: 16 }}>
            <Input
              placeholder={t('Search by Student Name')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: 200 }}
            />
            <Input
              placeholder={t('Search by Teacher Name')}
              value={searchTeacher}
              onChange={(e) => setSearchTeacher(e.target.value)}
              style={{ width: 220 }}
            />
            <RangePicker onChange={handleDateRangeChange} />
            <CSVLink data={currentItems} filename="student_feedback_report.csv">
            <Button icon={<FileExcelOutlined />} type="primary">
                {t('Export CSV')}
            </Button>
            </CSVLink>
            <Button
                icon={<ReloadOutlined />}
                onClick={fetchFeedbacks}
            >
                {t('Refresh')}
            </Button>
          </Space>

          <Table
            columns={columns as any}
            dataSource={currentItems}
            pagination={false}
            rowKey={(record: any, index: any) => index}
            scroll={{ x: 'max-content' }}
            style={{
              overflowX: 'auto',
              width: '100%',
              maxWidth: '100%'
            }}
          />
          <Pagination
            current={currentPage}
            total={filteredFeedbacks.length}
            pageSize={itemsPerPage}       
            onChange={setCurrentPage}
            style={{ marginTop: 16 }}
            />
        </Content>
        </Card>
      </Layout>
      <Drawer
  title="Edit Feedback"
  open={editDrawerVisible}
  onClose={() => setEditDrawerVisible(false)}
  width={600}
  footer={
    <div style={{ textAlign: 'right' }}>
      <Button onClick={() => setEditDrawerVisible(false)} style={{ marginRight: 8 }}>
        Cancel
      </Button>
      <Button
        type="primary"
        onClick={handleSubmitFeedbackUpdate}
        disabled={selectedFeedback?.approve !== 'pending'}
      >
        Save
      </Button>
    </div>
  }
>
  {selectedFeedback && (
    selectedFeedback.approve !== 'pending' ? (
      <Tag color="green" style={{ marginBottom: 16 }}>Approved</Tag>
    ) : (
      <Tag color="orange" style={{ marginBottom: 16 }}>Pending Approval</Tag>
    )
  )}

  <Form layout="vertical" disabled={selectedFeedback?.approve !== 'pending'}>
    <Form.Item label="Warmup">
      <Input.TextArea
        value={feedbackFormData.warmup}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, warmup: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Task 1">
      <Input.TextArea
        value={feedbackFormData.task_1}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, task_1: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Task 2">
      <Input.TextArea
        value={feedbackFormData.task_2}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, task_2: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Task 3">
      <Input.TextArea
        value={feedbackFormData.task_3}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, task_3: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Homework">
      <Input.TextArea
        value={feedbackFormData.homework}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, homework: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Reflection">
      <Input.TextArea
        value={feedbackFormData.reflection}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, reflection: e.target.value })}
      />
    </Form.Item>

    <Form.Item label="Comment">
      <Input.TextArea
        value={feedbackFormData.comment}
        onChange={(e) => setFeedbackFormData({ ...feedbackFormData, comment: e.target.value })}
      />
    </Form.Item>
  </Form>
</Drawer>

{printRecord && (
  <FeedbackPrintModal
    visible={printModalVisible}
    onClose={() => setPrintModalVisible(false)}
    feedback={printRecord}
    classInfo={{
      class_code: printRecord.class_code,
      subject_name: printRecord.subject_name,
      schedule_time: printRecord.schedule_time,
      schedule_hour: printRecord.schedule_hour,
    }}
    teacherInfo={{
      firstname: printRecord.teacher_firstname,
      lastname: printRecord.teacher_lastname,
      nickname: printRecord.teacher_nickname,
    }}
    studentInfo={{
      firstname: printRecord.student_firstname,
      lastname: printRecord.student_lastname,
      nickname: printRecord.student_nickname,
    }}
  />
)}

    </Layout>
    
  );
};

export default withRoleAccess(StudentFeedbackReport, ['admin', 'superadmin']);
